/**
 * ACT2-1108: what someone without the RudderStack permission to create warehouse accounts (ACT2-1276) sees
 * on the chat home while the workspace has no warehouse. It asks them to find someone who has it, and
 * lists the workspace's owner and admins as people who may be able to help.
 */
import { useQuery } from '@tanstack/react-query';
import { Database } from 'lucide-react';

import { Avatar, AvatarFallback, AvatarImage } from '@/_shared/components/ui/avatar';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/_shared/components/ui/empty';
import { Skeleton } from '@/_shared/components/ui/skeleton';
import { useOrpc } from '@/_shared/hooks/useOrpc';

export function NoWarehouseMemberNotice(): React.ReactElement {
  const orpc = useOrpc();
  const { data, isPending } = useQuery(orpc.workspace.get.queryOptions({ input: {} }));
  const admins = (data?.members ?? []).filter((member) => member.role === 'owner' || member.role === 'admin');

  return (
    <Empty className="border">
      <EmptyHeader>
        <EmptyMedia variant="icon">
          <Database />
        </EmptyMedia>
        <EmptyTitle>No warehouse connected yet</EmptyTitle>
        <EmptyDescription>
          Ask someone who can create warehouse accounts in RudderStack to connect one. Chat opens once a warehouse is
          connected.
        </EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        {isPending ? (
          <Skeleton className="h-10 w-56" />
        ) : admins.length > 0 ? (
          <div className="flex w-full max-w-sm flex-col gap-2 text-left">
            <p className="text-xs text-muted-foreground">Workspace admins who may be able to help:</p>
            <ul aria-label="Workspace admins who may be able to help" className="flex flex-col gap-2">
              {admins.map((admin) => {
                const name = admin.user.displayName ?? admin.user.githubLogin ?? admin.user.email ?? 'Admin';
                return (
                  <li key={admin.userId} className="flex min-w-0 items-center gap-3">
                    <Avatar className="size-8 shrink-0">
                      <AvatarImage src={admin.user.avatarUrl ?? admin.user.githubAvatarUrl ?? undefined} alt="" />
                      <AvatarFallback>{name[0]?.toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <span className="flex min-w-0 flex-col">
                      <span className="truncate text-sm font-medium text-foreground">{name}</span>
                      {admin.user.email && (
                        <span className="truncate text-xs text-muted-foreground">{admin.user.email}</span>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}
      </EmptyContent>
    </Empty>
  );
}
